import React from "react";
import { QrCodeResponse } from "@/interface/api/getAllQrCodes/types";

import { BusinessHoursDays } from "@/components/BusinessHours/types";
import { QRDownloadFormat, QrCodeProps } from "@/types/qr";

import { socials } from "@/internals/constants/socials";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { INITIAL_QR_DATA, QREditorContext } from "@/contexts/QREditorContext";

import { QREditorAPItoDataConversor } from "@/services/ApiConversorService/QREditor";

import { isClient } from "./browser";

import { onBindClick } from "./formHelper";
import {
  themePickerCustomColors,
  themePickerInitialColors,
} from "@/components/ThemePicker/ThemePicker";

import { isStaticQr } from "./qrContentType";
import { domains, DomainsType } from "@/sites/domains";
import { getSiteDomain } from "./site";

export const onBindSocialNetworks = (
  dataWithDefaultValues: any,
  componentKey: string,
  localStorageKey?: string,
  ommitedValues?: string[],
  links?: boolean,
  newData?: QRProps<"links" | "social">
) => {
  const qrEditorContext = React.useContext<QREditorContextProps<any>>(QREditorContext);

  onBindClick(dataWithDefaultValues, componentKey || "qrWebsiteSocials", (data: any[]) => {
    qrEditorContext?.setQrData(
      {
        ...qrEditorContext?.qrData,
        data: {
          ...qrEditorContext?.qrData.data,
          [localStorageKey]: [...data],
          ...(links && { links: newData?.data?.links }),
        },
      },
      ommitedValues || socials.map((social) => `data.${social.id}`)
    );
  });
};

export const onBindThemePicker = (
  dataWithDefaultValues: any,
  componentKey: string,
  ommitedValues?: string[]
) => {
  const qrEditorContext = React.useContext<QREditorContextProps<any>>(QREditorContext);

  onBindClick(dataWithDefaultValues, componentKey || "qrwebsiteThemePicker", (colorKey: string) => {
    let themeColors = themePickerInitialColors;
    if (themePickerCustomColors.length > 0) {
      themeColors = themeColors.concat(themePickerCustomColors);
    }
    const theme = themeColors.find((theme) => theme.key === colorKey);
    qrEditorContext?.setQrData(
      {
        ...qrEditorContext?.qrData,
        data: {
          ...qrEditorContext?.qrData.data,
          themeColor: {
            buttonColor: theme?.primary,
            backgroundColor: theme?.secondary,
            isCustom: true,
          },
        },
      },
      ommitedValues
    );
  });
};

export const onBindImage = (
  dataWithDefaultValues: any,
  componentKey: string,
  ommitedValues?: string[]
) => {
  const qrEditorContext = React.useContext<QREditorContextProps<any>>(QREditorContext);
  const [imageBase64, setImageBase64] = React.useState("");

  onBindClick(
    dataWithDefaultValues,
    componentKey || "qrWebsiteInformationLogo",
    (imgBase64: string[]) => setImageBase64(imgBase64[0])
  );

  React.useEffect(() => {
    qrEditorContext?.setQrData(
      {
        ...qrEditorContext?.qrData,
        data: { ...qrEditorContext?.qrData.data, logoUrl: imageBase64 },
      },
      ommitedValues || ["data.logoUrl", "data.file"]
    );
  }, [imageBase64]);
};

export const buildQRWifiUrlFromQRProsp = (qrData: QRProps<"wifi">) => {
  const { qrNetworkSecurityType, qrNetworkName, qrNetworkPassword, qrNetworkHidden } = qrData.data;

  const securityType = qrNetworkSecurityType ?? "nopass";

  const passwordString = qrNetworkPassword ? `P:${qrNetworkPassword};` : "";

  const hiddenPassString = qrNetworkHidden ? "H:true" : "H:false";

  return `WIFI:T:${securityType};S:${qrNetworkName};${passwordString}${hiddenPassString};;`;
};

export const getQRCodeUrl = (qrData: QRProps<any>, isPreview?: boolean) => {
  const { type, qrCodeReference: qrCode, data } = qrData;

  const origin = isClient() ? (window.location.origin as DomainsType) : undefined;

  switch (type) {
    case "wifi":
      return buildQRWifiUrlFromQRProsp(qrData);
    case "text":
      return data?.message;
    case "email": {
      const emailSubject = encodeURIComponent(qrData?.data?.subject ?? "");
      const emailMessage = encodeURIComponent(qrData?.data?.message ?? "");

      return `mailto:${qrData?.data?.email}?subject=${emailSubject}&body=${emailMessage}`;
    }
    case "url-static":
      return data?.qrUrlLink;
    case "sms":
      return buildQRSmsUrl(data?.phoneNumber, data?.message);
    case "whatsapp":
      return buildQRWhatsappUrl(data.phone, data.message);

    default:
      return origin && qrCode
        ? `${getScanDomainBySite(origin)}${isPreview ? "/p/" : "/qr/"}${qrCode}`
        : getScanDomainBySite(origin);
  }
};

export const isValidQrReference = (reference: string) => {
  const referenceRegex = new RegExp("^[a-zA-Z0-9]{1,36}$");
  return referenceRegex.test(reference);
};

export const downloadQrAi = async (
  _data: any,
  url: string,
  extension: QRDownloadFormat,
  nameOfFile: string
) => {
  const fileData = {
    type: extension === "png" ? "image/png" : extension === "jpeg" ? "image/jpeg" : "image/webp",
    name:
      extension === "png"
        ? `${nameOfFile}.png`
        : extension === "jpeg"
          ? `${nameOfFile}.jpg`
          : `${nameOfFile}.webp`,
  };

  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileData.name;

    link.click();

    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error("Error downloading image:", err);
  }
};

export const areWeInTheEditor = (): boolean =>
  isClient() && window.location.pathname.includes("/qr-editor");

export const getPreviewFrameFontSize = (textLength?: number) => {
  let fontSize = "9px";

  if (textLength >= 12) {
    fontSize = "7px";
  }
  if (textLength >= 16) {
    fontSize = "5px";
  }
  if (textLength >= 23) {
    fontSize = "4px";
  }

  return fontSize;
};

export const getPreviewFramePrintFontSize = (textLength: number) => {
  let fontSize = "100px";

  if (textLength >= 12) {
    fontSize = "90px";
  }
  if (textLength >= 16) {
    fontSize = "60px";
  }
  if (textLength >= 23) {
    fontSize = "40px";
  }

  return fontSize;
};

export const buildQRWifiUrl = (qrData: QrCodeProps | QrCodeResponse) => {
  const securityType = qrData?.content?.["network-security-type"] ?? "nopass";

  const passwordString = qrData?.content?.["network-password"]
    ? `P:${qrData?.content?.["network-password"]};`
    : "";

  const hiddenPassString = qrData?.content?.["network-hidden-password"] ? "H:true" : "H:false";

  return `WIFI:T:${securityType};S:${qrData?.content?.["network-name"]};${passwordString}${hiddenPassString};;`;
};

export const buildQRWhatsappUrl = (phone: string, message: string) => {
  const formattedPhone = getFormattedPhone(phone);
  const formattedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}?text=${formattedMessage}`;
};

export const buildQRTextUrl = (qrData: QrCodeProps | QrCodeResponse) => {
  return `${qrData?.content?.["message"]}`;
};

export const buildQREmailUrl = (qrData: QrCodeProps | QrCodeResponse) => {
  const emailSubject = encodeURIComponent(qrData?.content?.["email-subject"] ?? "");
  const emailMessage = encodeURIComponent(qrData?.content?.message ?? "");
  return `mailto:${qrData?.content?.["email-address"]}?subject=${emailSubject}&body=${emailMessage}`;
};

export const buildQRUrlStatic = (qrData: QrCodeProps | QrCodeResponse) => {
  return `${qrData?.content?.["field-url"]}`;
};

export const buildQRSmsUrl = (phoneNumber: string, message: string) => {
  return `sms:${getFormattedPhone(phoneNumber)}?body=${message}`;
};

export const buildStaticUrl = (qrData: QrCodeProps | QrCodeResponse) => {
  switch (qrData?.type.toLowerCase()) {
    case "wifi":
      return buildQRWifiUrl(qrData);
    case "text":
      return buildQRTextUrl(qrData);
    case "email":
      return buildQREmailUrl(qrData);
    case "url-static":
      return buildQRUrlStatic(qrData);
    case "sms":
      return buildQRSmsUrl(qrData?.content?.["sms-phone-number"], qrData?.content?.["message"]);
    case "whatsapp":
      return buildQRWhatsappUrl(qrData.content?.["phone"], qrData.content?.["message"]);
    default:
      return "";
  }
};

export const removeAppSubdomain = (urlString: string): string => {
  const url = new URL(urlString);
  const [subdomain, ...domainParts] = url.hostname.split(".");

  let cleanedHostname: string;

  if (subdomain === "app") {
    cleanedHostname = ["www", ...domainParts].join(".");
  } else if (subdomain.endsWith("-app")) {
    cleanedHostname = [subdomain.slice(0, -4), ...domainParts].join(".");
  } else {
    cleanedHostname = url.hostname;
  }

  const newUrl = new URL(url.toString());
  newUrl.hostname = cleanedHostname;

  const finalUrl = newUrl.toString();
  return finalUrl.endsWith("/") ? finalUrl.slice(0, -1) : finalUrl;
};

export const getScanDomainBySite = (site: DomainsType) => {
  const domain = getSiteDomain();

  const scanDomain = domains[domain]?.scanDomain;
  if (scanDomain) {
    site = site.replace(domain, scanDomain) as DomainsType;
  }
  return scanDomain ? removeAppSubdomain(site) : site;
};

export const buildPreviewUrl = (qrData: QrCodeProps | QrCodeResponse, location: string) => {
  return isStaticQr(qrData?.type)
    ? buildStaticUrl(qrData)
    : `${getScanDomainBySite(location as DomainsType)}/qr/${qrData?.reference}`;
};

const getFormattedPhone = (phone: string) => {
  return phone.replace(/[^+\d]+/g, "");
};

export const getEmptyDays = (): BusinessHoursDays => {
  return {
    monday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    tuesday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    wednesday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    thursday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    friday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    saturday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
    sunday: {
      enabled: false,
      times: [
        {
          start: "",
          end: "",
        },
      ],
    },
  };
};

export const isFileAblob = (file) => {
  const FILTER_PREFIX = "blob:";

  return typeof file === "string" && file.startsWith(FILTER_PREFIX);
};

const omittedLinks = (data: any) => {
  return data?.links?.length
    ? Array.from({ length: 10 }, (_, el) => {
        return data.links &&
          isFileAblob(data?.links[el]?.linkImageUrl && data?.links[el]?.linkImageUrl[0])
          ? [`data.text-link-${el}`, `data.url-link-${el}`, `data.links[${el}].linkImageUrl[0]`]
          : [`data.text-link-${el}`, `data.url-link-${el}`];
      }).flat()
    : [];
};

export const omittedValues = (data: any) => {
  return isFileAblob(data?.logoUrl)
    ? [
        ...socials.map((social) => `data.${social.id}`),
        "data.logoUrl",
        "data.file",
        "qrStyle.logoUrl",
        ...omittedLinks(data),
      ]
    : [...socials.map((social) => `data.${social.id}`), "data.file", ...omittedLinks(data)];
};

export const generateQrDataClone = (
  qrEditorContext: QREditorContextProps<any>,
  qrType: string,
  name?: string
): QRProps<any> => {
  const newQRData = qrEditorContext?.qrData
    ? { ...qrEditorContext.qrData }
    : qrEditorContext?.qrData;

  if (newQRData) {
    newQRData.type = qrType as any;
    newQRData.data = {
      ...INITIAL_QR_DATA.data,
      typeName: QREditorAPItoDataConversor.QRTypeTransform(qrType) || name,
    };
    newQRData.qrStyle = { ...INITIAL_QR_DATA.qrStyle, logoUrl: "" };
    newQRData.isValidForm = false;
    newQRData.qrCodeId = undefined;
    newQRData.qrCodeReference = undefined;
  }

  return newQRData;
};

export const setNewQRData = (qrEditorContext, type, name?) => {
  const newQRData = generateQrDataClone(qrEditorContext, type, name);

  qrEditorContext?.setQrData(newQRData);
};

export const strValid = (str: string, punctuation?: string): string =>
  str ? `${str}${punctuation ?? ""} ` : "";

export const getFullAddress = (address, numeration, city, zipCode, state, country) => {
  const zipCodeStr = zipCode ? `${zipCode} - ` : "";
  return (
    strValid(address, ",") +
    strValid(numeration, ",") +
    strValid(city, ",") +
    zipCodeStr +
    strValid(state, ",") +
    strValid(country)
  );
};

export const REFERENCE_EXITS = "reference_already_exists";
export const QR_REFERENCE_NOT_VALID = "invalid_qr_code_reference";

export const stringToBoolean = (value) => {
  if (typeof value === "string" && value === "true") return true;
  else if (typeof value === "string" && value === "false") return true;
};
